// Free training on a Khronos Timer: the log, checklist and Rest button the
// phone coach shows (#146, #157, #164), for a session the server's runner is
// timing. Sending to a timer hands the screen to LiveSessionFlow, but the
// checklist jotted before Start and the log kept as you go stay in the local
// coach state, so they carry across. Anything logged is stamped with the
// live session's clock rather than the idle local one.
//
// A Free training block kept going into after the finish (#199) gets the same,
// on a clock that starts with the block.

import React, { useContext } from "react";
import { WorkoutContext } from "../contexts/WorkoutContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import useWorkoutSettings from "./useWorkoutSettings";
import { addHrSample, emptyHrStats, isFreeTraining } from "../utils/freeTraining";
import { freeBlockAt } from "../utils/keepGoing";
import {
  buildExerciseList,
  checklistSessions,
  endRest,
  openRest,
  setRestTarget,
  startRest,
} from "../utils/freeTrainingLog";
import { zoneOf } from "../utils/heartRateZones";

/**
 * @param {object} args
 * @param {any} args.plan          the plan the session is running
 * @param {number} args.elapsed    the live session clock, in seconds
 * @param {any[]} [args.path]   the block's tree path, for a Free training block
 * @param {number} [args.blockStart] the session clock when that block began
 * @param {boolean} args.isRunning
 * @param {number|null} args.bpm
 * @param {any} args.hrBasis       heartRateBasis(profile)
 * @param {any} args.equipment     useConnectedEquipment()
 * @returns {{ freeTraining: any, freeLog: any[], freeChecklist: any,
 *   exerciseList: any[], updateLog: Function|null, reset: () => void }}
 */
export default function useLiveFreeTraining({
  plan,
  elapsed,
  path,
  blockStart = 0,
  isRunning,
  bpm,
  hrBasis,
  equipment,
}) {
  const { coachState, updateFreeLog, updateFreeChecklist, updateFreeRests } =
    useContext(WorkoutContext);
  const { sessions, performanceCapabilities } = useUserProfile();
  const [workoutSettings, updateWorkoutSettings] = useWorkoutSettings();
  const wholeSession = isFreeTraining(plan);
  const free = wholeSession || !!freeBlockAt(plan, path);
  const now = Math.max(0, Math.round((elapsed || 0) - (wholeSession ? 0 : blockStart || 0)));
  const nowRef = React.useRef(now);
  nowRef.current = now;

  const exerciseList = React.useMemo(
    () => (free ? buildExerciseList(sessions, performanceCapabilities) : []),
    [free, sessions, performanceCapabilities],
  );
  const checklistFrom = React.useMemo(
    () => (free ? checklistSessions(sessions) : []),
    [free, sessions],
  );

  const [hr, setHr] = React.useState(emptyHrStats);
  const bpmRef = React.useRef(bpm);
  bpmRef.current = bpm;
  React.useEffect(() => {
    if (!free || !isRunning) return undefined;
    const id = setInterval(() => {
      const b = bpmRef.current;
      setHr((stats) => addHrSample(stats, b, b && hrBasis ? zoneOf(b, hrBasis) : null));
    }, 1000);
    return () => clearInterval(id);
  }, [free, isRunning, hrBasis]);

  const updateLog = React.useCallback(
    (change) => updateFreeLog?.(change, nowRef.current),
    [updateFreeLog],
  );
  const updateRests = React.useCallback(
    (change) => updateFreeRests?.(change, nowRef.current),
    [updateFreeRests],
  );
  // Saved or discarded: the next go starts with an empty log. The checklist
  // stays, as it does after Start on the phone.
  const reset = React.useCallback(() => {
    updateFreeLog?.(() => []);
    updateFreeRests?.(() => []);
  }, [updateFreeLog, updateFreeRests]);

  const freeLog = coachState?.freeLog || [];
  const freeChecklist = coachState?.freeChecklist || null;
  const freeTraining = free
    ? {
        log: freeLog,
        list: exerciseList,
        onChange: updateLog,
        checklist: freeChecklist,
        onChecklistChange: updateFreeChecklist,
        checklistSessions: checklistFrom,
        rests: coachState?.freeRests || [],
        onRest: () =>
          updateRests((rests, at) =>
            openRest(rests)
              ? endRest(rests, at)
              : startRest(rests, at, workoutSettings.freeRestTarget),
          ),
        onRestTarget: (target) => {
          updateWorkoutSettings({ freeRestTarget: target });
          updateRests((rests) => setRestTarget(rests, target));
        },
        elapsed: now,
        hr,
        zone: bpm && hrBasis ? zoneOf(bpm, hrBasis) : null,
        subscribe: equipment?.active?.subscribe,
        hrSource: equipment?.hr,
      }
    : null;

  return {
    freeTraining,
    freeLog,
    freeChecklist,
    exerciseList,
    updateLog: free ? updateLog : null,
    reset,
  };
}
